import { Copy, Check } from 'lucide-react';
import { useCallback } from 'react';
import useCopyState from './useCopyState';

interface ArtifactThemeSummaryProps {
  title: string;
  data: {
    themes: Array<{ title: string; body: string }>;
  };
}

function ArtifactThemeSummary({ title, data }: ArtifactThemeSummaryProps) {
  const [copied, triggerCopied] = useCopyState();

  const handleCopy = useCallback(() => {
    const text = data.themes.map((t) => `**${t.title}**\n${t.body}`).join('\n\n');
    navigator.clipboard.writeText(`${title}\n\n${text}`);
    triggerCopied();
  }, [data, title, triggerCopied]);

  if (!data?.themes?.length) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <button
          type="button"
          onClick={handleCopy}
          className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
          title="Copy themes"
          aria-label="Copy themes"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-primary" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {data.themes.map((theme, idx) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          className="bg-white border border-neutral-200 rounded-lg p-4"
        >
          <h4 className="text-sm font-semibold text-neutral-800 mb-1.5">{theme.title}</h4>
          <p className="text-sm text-neutral-600 leading-relaxed">{theme.body}</p>
        </div>
      ))}
    </div>
  );
}

export default ArtifactThemeSummary;
